import React, { useState, useEffect, useCallback } from 'react';
import { MdArrowBack, MdAcUnit, MdInfoOutline, MdSettings } from 'react-icons/md';
import { useApp } from '../contexts/AppContext';
import { fetchCardStatus, executeAction } from '../services/backendAPI';

const CardManagementScreen = ({ onNavigate, onBack }) => {
  const { sessionId } = useApp();
  const [isFrozen, setIsFrozen] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [dispute, setDispute] = useState(null);
  const [busy, setBusy] = useState(false);

  // Reflect the real, ledger-backed frozen state (plan 270): if IVA froze the card
  // after a fraud alert, this screen shows it frozen (and the open dispute) so an
  // Unfreeze here actually reverses the flag on the server.
  const loadStatus = useCallback(async () => {
    if (!sessionId) return;
    const s = await fetchCardStatus(sessionId);
    if (s?.ready) {
      setIsFrozen(!!s.frozen);
      setDispute(Array.isArray(s.disputes) && s.disputes.length ? s.disputes[s.disputes.length - 1] : null);
    }
  }, [sessionId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Toggle freeze. When frozen on the backend, Unfreeze runs the ledger-backed
  // unfreeze_card action; otherwise fall back to a local-only toggle (demo).
  const toggleFreeze = useCallback(async () => {
    if (busy) { return; }
    if (sessionId && isFrozen) {
      setBusy(true);
      try {
        const res = await executeAction(sessionId, 'unfreeze_card', {});
        if (res?.ok) { setIsFrozen(false); setDispute(null); }
        else setIsFrozen((f) => !f); // no server freeze to reverse — just toggle locally
      } finally { setBusy(false); }
      return;
    }
    setIsFrozen((f) => !f);
  }, [sessionId, isFrozen, busy]);

  return (
    <div className={`screen bh-flow${isFrozen ? ' card-is-frozen' : ''}`}>
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Card Management</h1>
        </div>
      </header>

      <div className="bh-flow-body">
        {/* Card display */}
        <div className="bh-form-card" style={{ alignItems: 'center', gap: 'var(--space-sm)' }}>
          <div className={`card-display-container${isFrozen ? ' card-is-frozen' : ''}`} style={{ position: 'relative' }}>
            <div className={`aeroplan-card${isFrozen ? ' frozen-overlay' : ''}`}>
              <img
                src="/TD-Aeroplan-Visa-Infinite-Privilege-Card.jpeg"
                alt="TD Aeroplan Visa Infinite Privilege Card"
                className="card-image"
                width="320"
                height="200"
                style={{ borderRadius: 'var(--radius-lg)', display: 'block', maxWidth: '100%', height: 'auto' }}
              />
            </div>
          </div>

          {isFrozen && (
            <div className="bh-info-box" style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)', color: 'var(--td-blue)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2xs)' }}>
                <MdAcUnit size={18} />
                <span>This card is currently frozen and cannot be used.</span>
              </div>
              {dispute && (
                <span style={{ font: 'var(--bh-caption)' }}>
                  Dispute {dispute.caseId ? `${dispute.caseId} ` : ''}open on {dispute.merchant || 'a charge'} — under review. Unfreeze anytime if this was really you.
                </span>
              )}
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="bh-form-card">
          <div className="bh-action-row">
            <button className="bh-action-card" onClick={() => setShowPin(s => !s)}>
              <div className="bh-action-card-icon"><MdInfoOutline /></div>
              <span>{showPin ? 'Hide PIN' : 'Show PIN'}</span>
            </button>
            <button className="bh-action-card" onClick={toggleFreeze} disabled={busy}>
              <div className="bh-action-card-icon" style={{ background: isFrozen ? 'var(--td-blue)' : undefined, color: isFrozen ? 'var(--bh-white)' : undefined }}>
                <MdAcUnit />
              </div>
              <span>{isFrozen ? 'Unfreeze' : 'Freeze'}</span>
            </button>
            <button className="bh-action-card">
              <div className="bh-action-card-icon"><MdSettings /></div>
              <span>Settings</span>
            </button>
          </div>
        </div>

        {/* PIN — only revealed on tap */}
        {showPin && (
          <div className="bh-form-card" style={{ alignItems: 'center', gap: 'var(--space-2xs)' }}>
            <span style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>Card PIN</span>
            <span style={{ font: 'var(--bh-display)', letterSpacing: '0.4em', color: 'var(--bh-black)' }}>1 3 3 2</span>
            <span style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>Tap "Hide PIN" to keep this private</span>
          </div>
        )}

        {/* Card details */}
        <div className="bh-form-card">
          <h3 style={{ margin: 0, font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>Card Details</h3>
          <div className="bh-review-rows">
            <div className="bh-review-row">
              <span className="bh-review-row-label">Card</span>
              <span className="bh-review-row-value">TD Aeroplan Visa Infinite Privilege</span>
            </div>
            <div className="bh-review-row">
              <span className="bh-review-row-label">Status</span>
              <span className="bh-review-row-value" style={{ color: isFrozen ? 'var(--td-blue)' : 'var(--td-green)' }}>
                {isFrozen ? 'Frozen' : 'Active'}
              </span>
            </div>
            <div className="bh-review-row">
              <span className="bh-review-row-label">Network</span>
              <span className="bh-review-row-value">Visa Infinite Privilege</span>
            </div>
            <div className="bh-review-row">
              <span className="bh-review-row-label">Rewards</span>
              <span className="bh-review-row-value">Aeroplan</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardManagementScreen;
